import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Zap, RotateCcw, Plus, Target, Activity, Sun, SunDim } from 'lucide-react';
import axios from 'axios';
import { useWebSocket } from '../hooks/useWebSocket';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TreeOperator = () => {
  const [treeState, setTreeState] = useState({
    current_count: 0,
    tesla_triggered: false,
    super_glow_active: false
  });
  const [loading, setLoading] = useState(false);
  const { lastMessage, isConnected } = useWebSocket();

  useEffect(() => {
    fetchState();
  }, []);

  useEffect(() => {
    if (!lastMessage) return;
    
    if (lastMessage.type === 'tree_update') {
      setTreeState(prev => ({ ...prev, current_count: lastMessage.current_count }));
    } else if (lastMessage.type === 'tesla_trigger') {
      setTreeState(prev => ({ ...prev, tesla_triggered: true }));
    } else if (lastMessage.type === 'super_glow') {
      setTreeState(prev => ({ ...prev, super_glow_active: lastMessage.active }));
    } else if (lastMessage.type === 'tree_reset') {
      setTreeState({ current_count: 0, tesla_triggered: false, super_glow_active: false });
    } else if (lastMessage.type === 'init' && lastMessage.tree) {
      setTreeState(lastMessage.tree);
    }
  }, [lastMessage]);

  const fetchState = async () => {
    try {
      const res = await axios.get(`${API}/tree/state`);
      setTreeState(res.data);
    } catch (e) {
      console.error('Failed to fetch tree state:', e);
    }
  };

  const handleAction = async (action, data = {}) => {
    setLoading(true);
    try {
      await axios.post(`${API}/tree/${action}`, data);
      await fetchState();
    } catch (e) {
      console.error(`Action ${action} failed:`, e);
    }
    setLoading(false);
  };

  const energy = treeState.current_count / 320;

  return (
    <div className="operator-layout" data-testid="tree-operator">
      <div className="wave-background opacity-30" />
      
      <div className="relative z-10 max-w-4xl mx-auto">
        {/* Header */}
        <div className="operator-header">
          <div className="flex items-center gap-4">
            <Link to="/" className="nav-link" data-testid="back-link">
              <ArrowLeft size={20} />
              Back
            </Link>
            <h1 className="text-2xl font-bold text-white font-['Outfit']">
              Tree Operator
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <div className={`status-badge ${isConnected ? 'active' : 'eliminated'}`}>
              <Activity size={14} />
              {isConnected ? 'Connected' : 'Disconnected'}
            </div>
            <Link 
              to="/tree" 
              target="_blank"
              className="operator-btn secondary"
              data-testid="view-screen-link"
            >
              View Screen
            </Link>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          <div className="stat-card" data-testid="stat-participants">
            <div className="stat-value">{treeState.current_count}</div>
            <div className="stat-label">Participants</div>
          </div>
          <div className="stat-card" data-testid="stat-energy">
            <div className="stat-value">{Math.round(energy * 100)}%</div>
            <div className="stat-label">Energy Level</div>
          </div>
          <div className="stat-card" data-testid="stat-tesla">
            <div className="stat-value">
              {treeState.tesla_triggered ? (
                <span className="text-blue-400">ACTIVE</span>
              ) : (
                <span className="text-slate-500">READY</span>
              )}
            </div>
            <div className="stat-label">Tesla Status</div>
          </div>
          <div className="stat-card" data-testid="stat-super-glow">
            <div className="stat-value">
              {treeState.super_glow_active ? (
                <span className="text-yellow-400">GLOWING</span>
              ) : (
                <span className="text-slate-500">OFF</span>
              )}
            </div>
            <div className="stat-label">Super Glow</div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="operator-card p-6 mb-8">
          <div className="flex justify-between mb-2">
            <span className="text-slate-400 text-sm">Energy Progress</span>
            <span className="text-white font-semibold">{treeState.current_count} / 320</span>
          </div>
          <div className="h-4 bg-slate-800 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-blue-600 to-blue-400 transition-all duration-500"
              style={{ width: `${energy * 100}%` }}
            />
          </div>
          <div className="flex justify-between mt-2 text-xs text-slate-500">
            <span>0</span>
            <span className="text-yellow-500">280 (Tesla)</span>
            <span>320</span>
          </div>
        </div>

        {/* Super Glow Controls - Prominent Section */}
        <div className="operator-card p-6 mb-8 border-2 border-yellow-500/30">
          <h2 className="text-lg font-semibold text-yellow-400 mb-4 flex items-center gap-2">
            <Sun size={20} />
            Super Glow Controls
          </h2>
          
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => handleAction('super-glow')}
              className={`operator-btn ${treeState.super_glow_active ? 'secondary' : ''}`}
              disabled={loading || treeState.super_glow_active}
              data-testid="trigger-super-glow-btn"
              style={!treeState.super_glow_active ? { background: 'linear-gradient(135deg, #F59E0B, #D97706)' } : {}}
            >
              <Sun size={18} className="inline mr-2" />
              Trigger Super Glow
            </button>

            <button
              onClick={() => handleAction('reset-super-glow')}
              className="operator-btn secondary"
              disabled={loading || !treeState.super_glow_active}
              data-testid="reset-super-glow-btn"
            >
              <SunDim size={18} className="inline mr-2" />
              Reset Super Glow
            </button>
          </div>
        </div>

        {/* Regular Controls */}
        <div className="operator-card p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Participant Controls</h2>
          
          <div className="operator-grid">
            <button
              onClick={() => handleAction('reset')}
              className="operator-btn danger"
              disabled={loading}
              data-testid="reset-btn"
            >
              <RotateCcw size={18} className="inline mr-2" />
              Reset All
            </button>

            <button
              onClick={() => handleAction('increment', { step: 10 })}
              className="operator-btn"
              disabled={loading}
              data-testid="add-10-btn"
            >
              <Plus size={18} className="inline mr-2" />
              +10 Participants
            </button>

            <button
              onClick={() => handleAction('increment', { step: 50 })}
              className="operator-btn"
              disabled={loading}
              data-testid="add-50-btn"
            >
              <Plus size={18} className="inline mr-2" />
              +50 Participants
            </button>

            <button
              onClick={() => handleAction('demo-ramp', { target_count: 150, duration_ms: 5000 })}
              className="operator-btn secondary"
              disabled={loading}
              data-testid="charge-150-btn"
            >
              <Target size={18} className="inline mr-2" />
              Charge to 150 (5s)
            </button>

            <button
              onClick={() => handleAction('demo-ramp', { target_count: 280, duration_ms: 10000 })}
              className="operator-btn secondary"
              disabled={loading}
              data-testid="charge-280-btn"
            >
              <Target size={18} className="inline mr-2" />
              Charge to 280 (10s)
            </button>

            <button
              onClick={() => handleAction('tesla')}
              className={`operator-btn ${treeState.tesla_triggered ? 'secondary' : ''}`}
              disabled={loading || treeState.tesla_triggered}
              data-testid="tesla-btn"
            >
              <Zap size={18} className="inline mr-2" />
              {treeState.tesla_triggered ? 'Tesla Triggered' : 'Manual Tesla'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TreeOperator;
